import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Pencil, Trash2, RotateCcw } from 'lucide-react';
import { ExpenseCategory, ExpenseCategoriesApi } from '@/services/expenses.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field, Input, Textarea } from '@/components/ui/Input';

const categorySchema = z.object({
  name: z.string().min(1, 'Required'),
  description: z.string().optional(),
});
type FormValues = z.infer<typeof categorySchema>;

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

/**
 * Expense category reference data — migrated from legacy (654 rows/54
 * schools). Trash/restore is legacy's own lifecycle: the first Delete
 * click trashes (soft-delete), a second click on an already-trashed row
 * permanently deletes — blocked if any expense still uses it.
 */
export function ExpenseCategoriesPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'active' | 'trash'>('active');
  const [modal, setModal] = useState<{ open: boolean; category?: ExpenseCategory }>({ open: false });
  const [confirm, setConfirm] = useState<{ open: boolean; category?: ExpenseCategory }>({ open: false });

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['expense-categories', tab],
    queryFn: () => ExpenseCategoriesApi.list(tab === 'trash'),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['expense-categories'] });

  const upsert = useMutation({
    mutationFn: (vals: { id?: string; payload: { name: string; description?: string } }) =>
      vals.id ? ExpenseCategoriesApi.update(vals.id, vals.payload) : ExpenseCategoriesApi.create(vals.payload),
    onSuccess: () => {
      invalidate();
      setModal({ open: false });
    },
  });

  const removeMut = useMutation({
    mutationFn: (id: string): Promise<{ trashed: true } | { deleted: true }> =>
      tab === 'trash' ? ExpenseCategoriesApi.permanentDelete(id) : ExpenseCategoriesApi.remove(id),
    onSuccess: () => {
      invalidate();
      setConfirm({ open: false });
    },
  });

  const restoreMut = useMutation({
    mutationFn: (id: string) => ExpenseCategoriesApi.restore(id),
    onSuccess: invalidate,
  });

  return (
    <>
      <PageHeader
        title="Expense Categories"
        description="Categories used to classify expenses (e.g. Rent, Salary and allowance, Electricity charge)."
        actions={
          tab === 'active' && (
            <button className="btn-primary" onClick={() => setModal({ open: true })}>
              <Plus className="mr-1.5 h-4 w-4" /> New category
            </button>
          )
        }
      />

      <div className="mb-4 flex items-center gap-1 border-b border-slate-200">
        {(['active', 'trash'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium capitalize ${
              tab === t ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t === 'active' ? 'Active' : 'Trash'}
          </button>
        ))}
      </div>

      <DataTable<ExpenseCategory>
        rows={categories}
        getRowId={(r) => r.id}
        isLoading={isLoading}
        emptyMessage={tab === 'active' ? 'No expense categories yet. Create your first one.' : 'Trash is empty.'}
        columns={[
          { key: 'name', header: 'Name', render: (c) => <span className="font-medium text-slate-900">{c.name}</span> },
          { key: 'description', header: 'Description', render: (c) => c.description ?? '—' },
        ]}
        actions={(c) =>
          tab === 'active' ? (
            <>
              <button
                className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600"
                onClick={() => setModal({ open: true, category: c })}
                title="Edit"
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
                onClick={() => setConfirm({ open: true, category: c })}
                title="Trash"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <button
                className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600"
                onClick={() => restoreMut.mutate(c.id)}
                title="Restore"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              <button
                className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
                onClick={() => setConfirm({ open: true, category: c })}
                title="Delete permanently"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          )
        }
      />

      <CategoryFormModal
        open={modal.open}
        category={modal.category}
        onClose={() => setModal({ open: false })}
        onSubmit={(vals) => upsert.mutate({ id: modal.category?.id, payload: { ...vals, description: vals.description || undefined } })}
        saving={upsert.isPending}
        errorMsg={errMsg(upsert.error)}
      />

      <ConfirmDialog
        open={confirm.open}
        onClose={() => setConfirm({ open: false })}
        onConfirm={() => confirm.category && removeMut.mutate(confirm.category.id)}
        loading={removeMut.isPending}
        title={tab === 'active' ? 'Move to trash?' : 'Delete permanently?'}
        message={
          errMsg(removeMut.error) ??
          (tab === 'active'
            ? `"${confirm.category?.name}" will move to Trash. You can restore it later.`
            : `This will permanently delete "${confirm.category?.name}". Blocked if any expense still uses this category.`)
        }
        confirmText={tab === 'active' ? 'Move to trash' : 'Delete permanently'}
      />
    </>
  );
}

function CategoryFormModal({
  open,
  category,
  onClose,
  onSubmit,
  saving,
  errorMsg,
}: {
  open: boolean;
  category?: ExpenseCategory;
  onClose: () => void;
  onSubmit: (v: FormValues) => void;
  saving: boolean;
  errorMsg?: string;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(categorySchema),
    values: { name: category?.name ?? '', description: category?.description ?? '' },
  });

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={category ? `Edit ${category.name}` : 'New expense category'}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={handleSubmit(onSubmit)} disabled={saving}>
            {saving ? 'Saving…' : category ? 'Save changes' : 'Create category'}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Field label="Name" required error={errors.name?.message}>
          <Input {...register('name')} placeholder="Rent" />
        </Field>
        <Field label="Description" error={errors.description?.message}>
          <Textarea {...register('description')} rows={2} />
        </Field>
        {errorMsg && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errorMsg}</div>}
      </form>
    </Modal>
  );
}
